import React from "react";
import { cn, getBackgroundStyles, getBorderStyles } from "../../styles";
import { mdiClose } from "@mdi/js";
import { Button, ButtonVariant, Icon, InternalItemProps } from "../../atoms";
import { ThemeColor, BaseElementProps } from "../../props";

/**
 * Chip visual style variants.
 */
export enum ChipVariant {
  /** Chip with solid background and no border. */
  contained = "contained",

  /** Chip with border and transparent/light background. */
  outlined = "outlined",
}

export interface ChipProps extends BaseElementProps, InternalItemProps {
  /**
   * Main content of the Chip, can be text or any React node.
   */
  children?: React.ReactNode;

  /**
   * The thematic color of the componenet.
   * Should be a value from the imported `ThemeColor` enum.
   * @default ThemeColor.gray
   */
  color?: ThemeColor;

  /**
   * Visual style variant for the chip.
   * Should be a value from the imported `ChipVariant` enum.
   * @default ChipVariant.outlined
   */
  variant?: ChipVariant;

  /**
   * Callback function called when the action button (close/remove) is clicked.
   * When provided, displays a close button on the right side of the chip.
   * Typically used for removing or deleting the chip.
   */
  onAction?: () => void;

  /**
   * Additional props to customize the action button.
   * Allows overriding default button properties like color or className.
   */
  buttonProps?: React.ComponentProps<typeof Button>;
}

/**
 * Chip component for displaying compact information with optional icons and actions.
 * Provides a styled container with rounded corners that can include a leading icon
 * and/or a trailing action button for removal. Supports theme colors, variants
 * (contained/outlined), and dense sizing for flexible use in tags, filters, and selections.
 */
export const Chip = ({
  className = "",
  children,
  variant = ChipVariant.outlined,
  color = ThemeColor.gray,
  iconPath,
  iconName,
  iconLocal,
  onAction,
  buttonProps,
  dense = false,
  ...props
}: ChipProps) => {
  const hasIcon = !!(iconPath || iconName);
  const hasAction = !!onAction;

  const baseStyles = cn(
    "inline-flex items-stretch max-w-full",
    dense ? "text-xs rounded-xl" : "text-sm rounded-2xl",
    getBackgroundStyles(color),
    variant === ChipVariant.outlined && cn("border", getBorderStyles(color)),
  );

  const contentPaddingStyles = cn(
    "flex items-center min-w-0",
    dense ? "gap-1" : "gap-2",
    dense ? "py-1" : "py-1.5",
    !hasIcon && (dense ? "px-2" : "px-3"),
    hasIcon && !hasAction && (dense ? "pl-1.5 pr-2" : "pl-2 pr-3"),
    hasIcon && hasAction && (dense ? "px-1" : "px-1.5")
  );

  const iconSize = dense ? 12 : 16;

  return (
    <div className={cn(baseStyles, className)} {...props}>
      <div className={contentPaddingStyles}>
        <Icon
          path={iconPath}
          name={iconName}
          local={iconLocal}
          size={iconSize}
          color={color}
        />
        {children}
      </div>
      {hasAction && (
        <div className="flex items-center border-l border-current/10">
          <Button
            dense={dense}
            iconPath={mdiClose}
            variant={ButtonVariant.text}
            color={color}
            onClick={onAction}
            {...buttonProps}
            className={cn(
              "h-full rounded-l-none",
              dense ? "!p-1 rounded-r-xl" : "!p-1.5 rounded-r-2xl",
              buttonProps?.className
            )}
          />
        </div>
      )}
    </div>
  );
};

Chip.displayName = "Chip";
